<script setup>
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MediaLibraryPanel from 'dashboard/routes/dashboard/marketing/pages/MediaLibraryPanel.vue';
import ColorField from '../../fields/ColorField.vue';
import SpacingField from '../../fields/SpacingField.vue';
import SliderField from '../../fields/SliderField.vue';
import VisibilityField from '../../fields/VisibilityField.vue';

const props = defineProps({ node: { type: Object, required: true } });
const emit = defineEmits(['update']);
const ed = inject('emailEditor');
const { t } = useI18n();
const theme = computed(() => ed.template.value?.theme || {});
const st = computed(() => props.node.style || {});
const structures = computed(() => ed.catalog.value?.layout?.structures || {});
const footer = computed(() => props.node.role === 'footer');
const media = ref(null);
const style = (key, value) => emit('update', { style: { [key]: value } }, key);
const currentKey = computed(() =>
  Object.keys(structures.value).find(
    k =>
      JSON.stringify(structures.value[k]) ===
      JSON.stringify(props.node.columns.map(c => c.width))
  )
);
const pick = asset => {
  style('background_image', { asset_id: asset.id, url: asset.url });
  media.value?.close();
};
</script>

<template>
  <div data-props="row" class="flex flex-col gap-4">
    <div v-if="!footer" class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.PROPS.STRUCTURE')
      }}</span>
      <div class="grid grid-cols-3 gap-1.5">
        <button
          v-for="(widths, key) in structures"
          :key="key"
          :data-structure="key"
          type="button"
          class="flex h-8 gap-0.5 rounded-md border p-1"
          :class="currentKey === key ? 'border-n-brand' : 'border-n-weak'"
          :title="t(`EMAIL_TEMPLATES.EDITOR.STRUCTURE.${key}`)"
          @click="ed.setStructure(node.id, widths)"
        >
          <span
            v-for="(w, i) in widths"
            :key="i"
            class="rounded-sm bg-n-brand/25"
            :style="{ flexBasis: `${w}%` }"
          />
        </button>
      </div>
    </div>
    <ColorField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.BACKGROUND')"
      :theme="theme"
      allow-none
      :model-value="st.background"
      @update:model-value="v => style('background', v)"
    />
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="rounded-lg border border-n-weak px-2 py-1 text-xs"
        @click="media?.open()"
      >
        {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.BG_IMAGE') }}
      </button>
      <button
        v-if="st.background_image"
        type="button"
        class="text-xs text-n-ruby-11"
        @click="style('background_image', null)"
      >
        {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.REMOVE_IMAGE') }}
      </button>
    </div>
    <SpacingField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.PADDING')"
      :model-value="st.padding"
      @update:model-value="v => style('padding', v)"
    />
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.RADIUS')"
      :model-value="st.radius || 0"
      :min="0"
      :max="60"
      unit="px"
      @update:model-value="v => style('radius', v)"
    />
    <label class="flex items-center gap-2 text-xs text-n-slate-12">
      <input
        type="checkbox"
        :checked="st.stack_on_mobile !== false"
        @change="style('stack_on_mobile', $event.target.checked)"
      />
      {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.STACK') }}
    </label>
    <label class="flex items-center gap-2 text-xs text-n-slate-12">
      <input
        type="checkbox"
        :checked="!!st.reverse_on_mobile"
        @change="style('reverse_on_mobile', $event.target.checked)"
      />
      {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.REVERSE') }}
    </label>
    <VisibilityField
      v-if="!footer"
      :model-value="st.hide_on"
      @update:model-value="v => style('hide_on', v)"
    />
    <Dialog
      ref="media"
      :title="t('EMAIL_TEMPLATES.EDITOR.PROPS.BG_IMAGE')"
      :show-confirm-button="false"
      width="3xl"
      overflow-y-auto
    >
      <MediaLibraryPanel pickable @pick="pick" />
    </Dialog>
  </div>
</template>
